import * as THREE from 'three'
import { TweenLite, Power3, Power2, Power4 } from 'gsap'

import EventEmitter from './Utils/EventEmitter.js'
import Terrain from './Terrain.js'
import ProtectScene from './Scenes/Protect.js'
import ValidateScene from './Scenes/Validate.js'
import TrainingScene from './Scenes/Training.js'
import InvestigateScene from './Scenes/Investigate.js'
import { TerrainPlaneBufferGeometry } from './Geometries/TerrainPlane'

export default class World extends EventEmitter
{
    /**
     * Constructor
     */
    constructor(_options)
    {
        super()

        this.$eventHub = _options.$eventHub
        this.route = _options.route
        this.mobile = _options.mobile
        this.camera = _options.camera
        this.loader = _options.loader
        this.time = _options.time
        this.cursor = _options.cursor
        this.sizes = _options.sizes
        this.ui = _options.ui
        this.passes = _options.passes

        this.terrainSizes = {
            scene: 20,
            transition: 40,
            distanceBetween: 60
        }
        this.isFocus = false

        this.container = new THREE.Object3D()

        this.setScenes()
        this.setGlobalParticles()
        this.setPreviousParticles()
        this.setRGBOffset()
    }

    /**
     * Set RGB Offset
     */
    setRGBOffset()
    {
        this.rgbOffset = {}

        // Set up
        this.rgbOffset.mode = 'vector3' // vector3 | vector2
        this.rgbOffset.debug = false // Show a red cube
        this.rgbOffset.easing = 0.01
        this.rgbOffset.value = { x: 0.5 , y: 0.5 }
        this.rgbOffset.target = { x: 0.5 , y: 0.5 }

        this.rgbOffset.vector2 = new THREE.Vector2(0.5, 0.5)
        this.rgbOffset.projectionVector3 = new THREE.Vector3(0.0, 0.0, 0.0)

        // Vector3 dummy to test position
        this.rgbOffset.vector3Dummy = new THREE.Mesh(new THREE.BoxBufferGeometry(0.1, 0.1, 0.1, 1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true, visible: this.rgbOffset.debug }))
        this.rgbOffset.vector3Dummy.updateMatrixWorld()
        this.container.add(this.rgbOffset.vector3Dummy)

        // Positions
        this.rgbOffset.positions = {
            intro: { x: 0.2, y: 0.25, z: - 5.3 },
            protect: { x: 0.4, y: 0.1, z: - 5.3 },
            validate: { x: - 0.65, y: 0.13, z: this.terrainSizes.distanceBetween * 1 - 5.48 },
            training: { x: 0.95, y: 0.2, z: this.terrainSizes.distanceBetween * 2 - 4.9 },
            investigate: { x: 0, y: 2, z: this.terrainSizes.distanceBetween * 3 - 4.88 }
        }

        // Set start position
        this.rgbOffset.vector3Dummy.position.x = this.rgbOffset.positions[this.route.current.meta.experienceRgbOffset].x
        this.rgbOffset.vector3Dummy.position.y = this.rgbOffset.positions[this.route.current.meta.experienceRgbOffset].y
        this.rgbOffset.vector3Dummy.position.z = this.rgbOffset.positions[this.route.current.meta.experienceRgbOffset].z

        // Time tick
        this.time.on('tick', () =>
        {
            // Set target
            if(this.rgbOffset.mode === 'vector2')
            {
                this.rgbOffset.target.x = this.rgbOffset.vector2.x
                this.rgbOffset.target.y = this.rgbOffset.vector2.y
            }

            else if(this.rgbOffset.mode === 'vector3')
            {
                this.rgbOffset.projectionVector3.setFromMatrixPosition(this.rgbOffset.vector3Dummy.matrixWorld)
                const projection = this.rgbOffset.projectionVector3.project(this.camera.instance)

                if(!isNaN(projection.x) && projection.x !== Infinity && projection.x !== - Infinity)
                {
                    this.rgbOffset.target.x = projection.x + 0.5
                    this.rgbOffset.target.y = projection.y + 0.5
                }
            }

            // Set value
            this.rgbOffset.value.x += (this.rgbOffset.target.x - this.rgbOffset.value.x) * this.time.delta * this.rgbOffset.easing
            this.rgbOffset.value.y += (this.rgbOffset.target.y - this.rgbOffset.value.y) * this.time.delta * this.rgbOffset.easing

            this.passes.rgbOffset.material.uniforms.uRadialDumping.value.x = this.rgbOffset.value.x
            this.passes.rgbOffset.material.uniforms.uRadialDumping.value.y = this.rgbOffset.value.y
        })

        this.$eventHub.$on('experience.changeIndex', () =>
        {
            const position = this.rgbOffset.positions[this.scenes.current.name]
            TweenLite.to(this.rgbOffset.vector3Dummy.position, 3, { ...position, ease: Power3.easeInOut })
        })

        this.$eventHub.$on('experience.playFocusStart', () =>
        {
            this.rgbOffset.mode = 'vector2'

            const position = this.rgbOffset.positions[this.scenes.current.name]
            this.rgbOffset.vector3Dummy.position.x = position.x
            this.rgbOffset.vector3Dummy.position.y = position.y
            this.rgbOffset.vector3Dummy.position.z = position.z
        })

        this.$eventHub.$on('experience.quitFocusEnd', () =>
        {
            this.rgbOffset.mode = 'vector3'

            const position = this.rgbOffset.positions[this.scenes.current.name]
            this.rgbOffset.vector3Dummy.position.x = position.x
            this.rgbOffset.vector3Dummy.position.y = position.y
            this.rgbOffset.vector3Dummy.position.z = position.z
        })
    }

    /**
     * Set global particles
     */
    setGlobalParticles()
    {
        this.globalParticles = {}
        this.globalParticles.geometry = new THREE.Geometry()

        for(let i = 0; i < 1000; i++)
        {
            const x = (Math.random() - 0.5) * 20
            const y = Math.random() * 10 - 8
            const z = Math.random() * 220 - 5
            this.globalParticles.geometry.vertices.push(new THREE.Vector3(x, y, z))
        }

        this.globalParticles.material = new THREE.ShaderMaterial({
            transparent: true,
            uniforms:
            {
                uColor: { value: new THREE.Color(0xffe600) },
                uAlpha: { value: this.route.current.name === 'home' || this.route.current.name === 'slider' ? 0 : 1 },
                uTime: { value: 0 },
                uWind: { value: - 0.0008 } // - 0.0008
            },
            vertexShader: `
                #define M_PI 3.1415926535897932384626433832795

                uniform float uTime;
                uniform float uWind;

                varying float vAlpha;

                highp float random(vec2 co)
                {
                    highp float a = 12.9898;
                    highp float b = 78.233;
                    highp float c = 43758.5453;
                    highp float dt = dot(co.xy, vec2(a, b));
                    highp float sn = mod(dt, M_PI);

                    return fract(sin(sn) * c);
                }

                void main()
                {
                    vec4 modelPosition = modelMatrix * vec4(position, 1.0);
                    modelPosition.x = mod(modelPosition.x + uTime * ((uWind * 0.5) + (uWind * 0.5) * random(modelPosition.yz)), 20.0) - 10.0;
                    modelPosition.y = modelPosition.y + sin(modelPosition.x) * 0.3 + modelPosition.y + sin(modelPosition.x * 0.357) * 0.3;

                    vec4 viewPosition = viewMatrix * modelPosition;

                    float distance = distance(vec4(0.0, 0.0, 0.0, 0.0), viewPosition);

                    gl_PointSize = 1.5 - clamp((distance - 12.0) * 1.5, 0.0, 1.5);
                    // gl_PointSize = 1.0;
                    gl_Position = projectionMatrix * viewPosition;

                    vAlpha = 1.0 - clamp(distance * 0.5 / 12.0, 0.0, 1.0);
                    // vAlpha = 1.0;
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                uniform float uAlpha;

                varying float vAlpha;

                void main()
                {
                    gl_FragColor = vec4(uColor, vAlpha * uAlpha);
                    // gl_FragColor = vec4(uColor, 1.0);
                }
            `
        })
        this.globalParticles.points = new THREE.Points(this.globalParticles.geometry, this.globalParticles.material)
        this.globalParticles.points.frustumCulled = false
        this.container.add(this.globalParticles.points)

        this.time.on('tick', () =>
        {
            this.globalParticles.material.uniforms.uTime.value = this.time.elapsed
        })
    }

    /**
     * Set previous particles
     */
    setPreviousParticles()
    {
        this.previousParticles = {}
        this.previousParticles.geometry = new THREE.Geometry()

        // Before scene 1
        for(let i = 0; i < 3000; i++)
        {
            const angle = Math.random() * Math.PI * 2
            const radius = Math.random()
            const x = Math.cos(angle) * radius * 3
            const y = Math.sin(angle) * radius * 3
            const z = - Math.random() * 80 - 5.3
            this.previousParticles.geometry.vertices.push(new THREE.Vector3(x, y, z))
        }

        this.previousParticles.material = new THREE.ShaderMaterial({
            transparent: true,
            uniforms:
            {
                uColor: { value: new THREE.Color(0xffe600) },
                uAlpha: { value: this.route.current.name === 'home' || this.route.current.name === 'slider' ? 0 : 1 }
            },
            vertexShader: `
                #define M_PI 3.1415926535897932384626433832795

                varying float vAlpha;

                highp float random(vec2 co)
                {
                    highp float a = 12.9898;
                    highp float b = 78.233;
                    highp float c = 43758.5453;
                    highp float dt = dot(co.xy, vec2(a, b));
                    highp float sn = mod(dt, M_PI);

                    return fract(sin(sn) * c);
                }

                void main()
                {
                    vec4 modelPosition = modelMatrix * vec4(position, 1.0);
                    vec4 viewPosition = viewMatrix * modelPosition;

                    float distance = distance(vec4(0.0, 0.0, 0.0, 0.0), viewPosition);

                    gl_PointSize = 1.5 - clamp((distance - 12.0) * 1.5, 0.0, 1.5);
                    // gl_PointSize = 0.01;
                    gl_Position = projectionMatrix * viewPosition;

                    // vAlpha = clamp((1.0 - (gl_Position.z - 10.0)) * 0.2, 0.0, 1.0);
                    vAlpha = 1.0;
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                uniform float uAlpha;

                varying float vAlpha;

                void main()
                {
                    gl_FragColor = vec4(uColor, vAlpha * uAlpha);
                }
            `
        })
        this.previousParticles.points = new THREE.Points(this.previousParticles.geometry, this.previousParticles.material)
        this.previousParticles.points.frustumCulled = false
        this.container.add(this.previousParticles.points)
    }

    /**
     * Set scenes
     */
    setScenes()
    {
        // Set up
        this.scenes = {}
        this.scenes.all = []

        // Options
        const options = {
            $eventHub: this.$eventHub,
            mobile: this.mobile,
            camera: this.camera,
            loader: this.loader,
            time: this.time,
            cursor: this.cursor,
            sizes: this.sizes,
            ui: this.ui,
            terrainGeometry: new TerrainPlaneBufferGeometry(this.terrainSizes.scene, this.terrainSizes.scene, this.mobile ? 260 : 360, this.mobile ? 260 : 360, 4)
        }

        // Protect scene
        this.scenes.protect = new ProtectScene({ ...options, position: new THREE.Vector3(0, 0, 0) })
        this.container.add(this.scenes.protect.container)
        this.scenes.all.push(this.scenes.protect)

        // Validate scene
        this.scenes.validate = new ValidateScene({ ...options, position: new THREE.Vector3(0, 0, 1 * this.terrainSizes.distanceBetween) })
        this.container.add(this.scenes.validate.container)
        this.scenes.all.push(this.scenes.validate)

        // Training scene
        this.scenes.training = new TrainingScene({ ...options, position: new THREE.Vector3(0, 0, 2 * this.terrainSizes.distanceBetween) })
        this.container.add(this.scenes.training.container)
        this.scenes.all.push(this.scenes.training)

        // Investigate scene
        this.scenes.investigate = new InvestigateScene({ ...options, position: new THREE.Vector3(0, 0, 3 * this.terrainSizes.distanceBetween) })
        this.container.add(this.scenes.investigate.container)
        this.scenes.all.push(this.scenes.investigate)

        // Current
        this.scenes.current = this.scenes[this.route.current.meta.experienceFocusScene ? this.route.current.meta.experienceFocusScene : 'protect']

        this.$eventHub.$on('experience.changeIndex', (_index, _oldIndex) =>
        {
            // Hide / Show scenes
            this.scenes.all[_oldIndex].hide()
            this.scenes.all[_index].show()

            // Save
            this.scenes.current = this.scenes.all[_index]

            // Camera focus
            if(this.camera.type === 'guided')
            {
                this.camera.setState(`${this.scenes.current.name}${this.isFocus ? 'Focus' : 'Unfocus'}`, 3, Power2.easeInOut)
            }
        })
    }

    /**
     * Set terrain transitions
     */
    setTerrainTransitions()
    {
        this.terrainTransitions = {}
        this.terrainTransitions.all = []

        const options = {
            mobile: this.mobile,
            time: this.time,
            loader: this.loader,
            ui: this.ui,
            geometry: new TerrainPlaneBufferGeometry(this.terrainSizes.scene, this.terrainSizes.transition, 90, 90 * 2, 4),
            texture: this.loader.resources.terrainTransitionTexture
        }

        for(let i = 0 ; i < this.scenes.all.length - 1; i++)
        {
            const terrain = new Terrain(options)
            terrain.plane.material.uniforms.uElevationNoiseMultiplier.value = 0.2
            terrain.container.position.z = (i + 0.5) * this.terrainSizes.distanceBetween
            this.container.add(terrain.container)
            this.terrainTransitions.all.push(terrain)
        }

        this.time.on('tick', () =>
        {
            if(this.camera.type === 'guided')
            {
                for(const _terrain of this.terrainTransitions.all)
                {
                    const distance = _terrain.container.position.z - this.camera.transition.container.position.z
                    const alpha = (1 - distance / (this.terrainSizes.distanceBetween * 0.5)) * 2
                    _terrain.plane.material.uniforms.uAlpha.value = alpha

                    _terrain.plane.mesh.visible = _terrain.plane.material.uniforms.uAlpha.value > 0
                }
            }
        })
    }

    /**
     * Play loading
     */
    playLoading()
    {
        // Protect scene
        this.scenes.protect.terrain.reset()

        // Camera
        if(this.camera.type === 'guided')
        {
            this.camera.setState('protectPreIntro', 0)
        }
    }

    /**
     * Play intro
     */
    playIntro()
    {
        // Camera
        if(this.camera.type === 'guided')
        {
            this.camera.setState('protectPreIntro', 0, Power2.easeInOut)
            this.camera.setState('protectUnfocus', 4, Power4.easeOut)
        }

        // Focus
        TweenLite.to(this.rgbOffset.vector3Dummy.position, 4, { ...this.rgbOffset.positions.protect, ease: Power2.easeInOut })

        // Particles
        TweenLite.to(this.globalParticles.material.uniforms.uAlpha, 2, { value: 1 })
        TweenLite.to(this.previousParticles.material.uniforms.uAlpha, 2, { value: 1 })

        // Protect scene
        this.scenes.protect.introTimeline.seek(0)
        this.scenes.protect.introTimeline.play()
        window.requestAnimationFrame(() =>
        {
            this.scenes.protect.introTimeline.pause()
        })

        window.setTimeout(() =>
        {
            this.scenes.protect.introTimeline.play()

            // Event trigger
            window.setTimeout(() =>
            {
                this.$eventHub.$emit('experience.introEnd')
            }, 5000)

            this.$eventHub.$emit('experience.introStart')
        }, 100)
    }

    /**
     * Play focus
     */
    playFocus()
    {
        // Protect scene
        this.scenes.protect.introTimeline.seek('-=0', false)

        // Camera
        if(this.camera.type === 'guided')
        {
            this.camera.setState(`${this.scenes.current.name}Focus`, 2, Power2.easeInOut)
        }

        // Scenes
        this.scenes[this.route.current.meta.experienceFocusScene].focusTimeline.play()

        // Event trigger
        window.setTimeout(() =>
        {
            this.$eventHub.$emit('experience.playFocusEnd')
        }, 1500)

        this.$eventHub.$emit('experience.playFocusStart')

        // Save
        this.isFocus = true
    }

    /**
     * Quit focus
     */
    quitFocus()
    {
        // Camera
        if(this.camera.type === 'guided')
        {
            this.camera.setState(`${this.scenes.current.name}Unfocus`, 2, Power2.easeInOut)
        }

        // Scenes
        this.scenes[this.route.old.meta.experienceFocusScene].focusTimeline.reverse()

        // Event trigger
        window.setTimeout(() =>
        {
            this.$eventHub.$emit('experience.quitFocusEnd')
        }, 1500)

        this.$eventHub.$emit('experience.quitFocusStart')

        // Save
        this.isFocus = false
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/World.js